import { DekError, getMessage } from "./error.js";
class ResponseHandler {
    response;
    options;
    responseHandlerList = {};
    constructor(response) {
        this.response = response;
    }
    /**
     * Nastaví response
     * @param response - Response
     * @returns this pro method chaining
     */
    setResponse(response) {
        this.response = response;
        return this;
    }
    /**
     * Nastaví options
     * @param options - Options
     * @returns this pro method chaining
     */
    setOptions(options) {
        this.options = options;
        return this;
    }
    /**
     * Kontroluje, zda existují handlery pro daný status kód
     */
    hasResponseHandlers(statusCode, statusWildCard) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return !!this.responseHandlerList[statusCode] || !!this.responseHandlerList[statusWildCard];
    }
    setDefaultResponseHandler(callback) {
        if (this.hasResponseHandlers(200, '2xx'))
            return;
        this.addResponseHandler(200, callback);
    }
    /**
     * Přidá handler pro zpracování odpovědi s daným status kódem
     * @param statusCode - HTTP status kód nebo kategorie ('2xx', '4xx', '5xx')
     * @param callback - Funkce pro zpracování odpovědi
     * @returns this pro method chaining
     *
     * @example
     * ```typescript
     * api.addResponseHandler(404, async (response) => {
     *   console.log('Resource not found');
     *   return null;
     * });
     * ```
     */
    addResponseHandler(statusCode, callback) {
        this.responseHandlerList[statusCode] ??= [];
        this.responseHandlerList[statusCode].push(callback);
        return this;
    }
    /**
 * Spustí handlery pro daný status kód odpovědi
 */
    async runResponseHandlers() {
        if (!this.response)
            throw new DekError('Response není nastavena, byla setnutá?');
        const statusCode = this.response.status;
        const statusWildCard = `${Math.floor(statusCode / 100)}xx`;
        const hasHandlers = this.hasResponseHandlers(statusCode, statusWildCard);
        let data;
        if (hasHandlers) {
            this.responseHandlerList[statusCode] ??= [];
            this.responseHandlerList[statusWildCard] ??= [];
            for (const handler of this.responseHandlerList[statusCode]) {
                const result = await handler?.(this.response.clone());
                data ??= result;
            }
            for (const handler of this.responseHandlerList[statusWildCard]) {
                const result = await handler?.(this.response.clone());
                data ??= result;
            }
        }
        if (!hasHandlers && statusCode >= 400) {
            await this.handleError();
        }
        return data;
    }
    /**
     * Zpracuje chybu z response a zobrazí uživateli příslušnou zprávu
     * @param setting - Nastavení pro zobrazení chyby
     * @throws {DekError} S detaily chyby
     */
    async handleError(setting) {
        if (!this.response)
            throw new DekError('Response není nastavena, byla setnutá?');
        setting?.hideMessage !== true && await this.showMessage(setting?.customMessage);
        setting && (setting.options = setting.options ?? this.options);
        const filteredParams = setting?.options ? filterSensitiveData(setting.options) : undefined;
        const text = await this.response.clone().text();
        const errorMessage = getMessage(this.response.url, JSON.stringify(filteredParams), `Chyba: [${this.response.status}${this.response.statusText ? ` - ${this.response.statusText}` : ''}]`) + (text ? ` => ${text.slice(0, 100)}${text.length > 100 ? '...' : ''}` : '');
        throw new DekError(errorMessage, this.response.status);
    }
    async showMessage(customMessage) {
        if (!this.response)
            throw new DekError('Response není nastavena, byla setnutá?');
        if (customMessage === false)
            return;
        const defaultMessage = customMessage ?? (this.response.status in errorMessagesByStatus ? errorMessagesByStatus[this.response.status] : `Nastala neočekávaná chyba při požadavku na server: ${this.response.status}`);
        const text = await this.response.clone().text();
        // error který se zobrazí uživateli pomocí flash message
        // pokud je text prázdný, tak se zobrazí defaultní message => stejně není co parsovat
        // Pokud je error 500, tak se zobrazí defaultní message
        const skipParsing = this.response.status === 500
            // pokud je status v errorMessagesByStatus, tak se zobrazí message z errorMessagesByStatus
            || Object.keys(errorMessagesByStatus).includes(this.response.status.toString())
            // pokud je customMessage, zobrazí se customMessage
            || !!customMessage;
        if (text === '' || skipParsing) {
            this.dispatchError(defaultMessage);
        }
        else {
            try {
                await this.parseResponseForError() || this.dispatchError(defaultMessage);
            }
            catch (error) {
                // error pokud dojde k chybě při parsování json
                this.dispatchError(defaultMessage);
                throw new DekError('Chyba při ApiHandler.showMessage: ' + error.message);
            }
        }
    }
    async parseResponseForError() {
        if (!this.response)
            throw new DekError('Response není nastavena, byla setnutá?');
        const json = await this.response.clone().json();
        // if response is json and has message
        if (json.some(x => x.message)) {
            for (const { message } of json.filter(x => x.message)) {
                message && this.dispatchError(message);
            }
            return true;
        }
        else {
            return false;
        }
    }
    dispatchError(message) {
        dispatchEvent(new CustomEvent('ES_ADD_MESSAGE_FLASH', { detail: { message, type: 'error' } }));
    }
}
const errorMessagesByStatus = {
    429: 'Překročili jste limit požadavků. Zkuste to znovu později. (429)',
};
// Pomocná funkce pro filtrování citlivých údajů
function filterSensitiveData(obj) {
    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'credential'];
    const filtered = { ...obj };
    // Funkce pro filtrování objektu
    const filterObject = (obj) => {
        const result = {};
        for (const [key, value] of Object.entries(obj)) {
            if (sensitiveKeys.some(k => key.toLowerCase().includes(k))) {
                result[key] = '***FILTERED***';
            }
            else if (value && typeof value === 'object' && !Array.isArray(value)) {
                result[key] = filterObject(value);
            }
            else {
                result[key] = value;
            }
        }
        return result;
    };
    // Funkce pro zpracování JSON stringu
    const processJsonString = (str) => {
        try {
            const parsed = JSON.parse(str);
            const filtered = filterObject(parsed);
            return JSON.stringify(filtered);
        }
        catch {
            // Pokud se nejedná o validní JSON, vrátíme původní string
            return str;
        }
    };
    // Filtrování hlavních parametrů
    if (filtered.options) {
        const options = filtered.options;
        if (options.body) {
            if (typeof options.body === 'string') {
                options.body = processJsonString(options.body);
            }
            else if (typeof options.body === 'object' && !(options.body instanceof FormData)) {
                options.body = filterObject(options.body);
            }
        }
        filtered.options = filterObject(options);
    }
    if (filtered.parameters) {
        filtered.parameters = filterObject(filtered.parameters);
    }
    if (filtered.pathParameters) {
        filtered.pathParameters = filterObject(filtered.pathParameters);
    }
    if (filtered.body) {
        if (typeof filtered.body === 'string') {
            filtered.body = processJsonString(filtered.body);
        }
        else if (typeof filtered.body === 'object' && !(filtered.body instanceof FormData)) {
            filtered.body = filterObject(filtered.body);
        }
    }
    return filtered;
}
export { ResponseHandler, };
